<script setup lang="ts">
import { useStore } from 'vuex';
import BaseLayout from '@/layouts/BaseLayout.vue';
import {
  Button,
  DeviceOptionContent,
  DEVICE_OPTION_CONTENT_ROW_PX,
  GpxSelect,
  type TrackerPickerDevice,
} from '@gpx-spa/ui';
import VReportTableItem from '@/components/report/list/ReportListItem.vue';
import { setTitle } from '@/utils/title';
import { useToast } from 'vue-toastification';
import { COMMON_SAVE_SUCCESS } from '@/config/constants';
import {
  ACCOUNT_DEVICE_LIST_PER_PAGE,
  fetchAccountDeviceListPage,
} from '@gpx-spa/queries';
import { computed, reactive } from 'vue';

const toast = useToast();

setTitle('Reports');

const data = reactive({
  form: {
    deviceId: null as number | null,
    date: [] as string[],
    include_positions: true,
    include_sensors: true,
    include_alerts: true,
    include_geofence_name: true,
  },
  isSaving: false,
  columns: ['Tracker', 'Date Range', 'Public?', 'Public Link'],
});

const store = useStore();

const accountId = computed(() => store.getters['auth/accountId'] as number);
const reports = computed(() => store.getters['report/all']);

const deviceListParams = {
  subscription_status: 'active,pending-renewal',
};

const fetchDevicePage = ({
  page,
  perPage,
  query,
}: {
  page: number;
  perPage: number;
  query: string;
}) =>
  fetchAccountDeviceListPage(
    accountId.value,
    { page, perPage, query },
    deviceListParams,
  );

const isSaveDisabled = computed(() => !data.form.deviceId || data.isSaving);

const handleSave = () => {
  data.isSaving = true;
  store
    .dispatch('report/store', {
      accountId: accountId.value,
      params: {
        include: 'device',
        device_id: data.form.deviceId,
        start_at: data.form.date[0],
        end_at: data.form.date[1],
        include_positions: data.form.include_positions,
        include_sensors: data.form.include_sensors,
        include_alerts: data.form.include_alerts,
        include_geofence_name: data.form.include_geofence_name,
      },
    })
    .then(() => {
      data.form.deviceId = null;
      toast.success(COMMON_SAVE_SUCCESS);
    })
    .finally(() => (data.isSaving = false));
};
</script>
<template>
  <BaseLayout>
    <h4 class="mb-2 text-2xl font-medium leading-tight">Reports</h4>
    <div
      v-if="$can('manage', 'Reports')"
      class="flex flex-wrap items-center"
    >
      <div class="mb-4 mr-6 w-full sm:mb-0 sm:w-auto sm:min-w-0">
        <div class="sm:flex sm:items-center">
          <label
            for="report-tracker-select"
            class="mb-2 inline-block sm:mb-0 sm:mr-4"
          >
            Tracker:
          </label>
          <GpxSelect
            id="report-tracker-select"
            v-model="data.form.deviceId"
            remote
            :fetch-page="fetchDevicePage"
            :per-page="ACCOUNT_DEVICE_LIST_PER_PAGE"
            :item-size="DEVICE_OPTION_CONTENT_ROW_PX"
            value-key="id"
            label-key="name"
            filter
            filter-placeholder="Search tracker..."
            placeholder="Select tracker"
            size="small"
            scroll-height="280px"
            reset-filter-on-hide
            class="w-[400px] max-w-full"
            :overlay-style="{ maxWidth: '400px' }"
            aria-label="Tracker"
          >
            <template #option="{ option, filterQuery, selected }">
              <DeviceOptionContent
                v-if="option"
                :device="option as TrackerPickerDevice"
                :search-query="filterQuery"
                :selected="selected"
              />
            </template>
          </GpxSelect>
        </div>
      </div>
      <div class="mb-5 mr-2 w-full sm:mb-0 sm:w-auto">
        <div class="flex flex-wrap items-center sm:flex-nowrap">
          <span
            class="mb-2 block w-full sm:mb-0 sm:mr-4 sm:inline-block sm:w-auto"
          >
            Date range:
          </span>
          <VDatePickerRange
            :show-tools="false"
            v-model="data.form.date"
            :maxDate="true"
            tooltip="Select range"
            utc
          />
        </div>
      </div>
      <Button
        size="sm"
        :loading="data.isSaving"
        loadingText="Saving..."
        :disabled="isSaveDisabled"
        @click.prevent="handleSave"
      >
        Save report
      </Button>
    </div>
    <div v-if="!reports.length" class="py-12 text-center">
      <h3 class="text-2xl leading-tight">No reports have been created</h3>
    </div>
    <table v-else class="mt-12 table w-full">
      <thead
        class="hidden bg-[#f9f9f9] text-left text-base leading-[1.5] text-gpx_black-200 sm:table-header-group"
      >
        <tr>
          <th class="border-y border-[#dee2e6] p-[12px] font-medium">
            Tracker
          </th>
          <th class="border-y border-[#dee2e6] p-[12px] font-medium">
            Date Range
          </th>
          <th class="border-y border-[#dee2e6] p-[12px] font-medium">
            Public?
          </th>
          <th class="border-y border-[#dee2e6] p-[12px] font-medium">
            Public Link
          </th>
        </tr>
      </thead>
      <tbody
        class="flex flex-col border-t border-[#dee2e6] sm:table-row-group sm:border-t-0"
      >
        <VReportTableItem
          v-for="report in reports"
          :key="report.id"
          :report="report"
        />
      </tbody>
    </table>
  </BaseLayout>
</template>
